import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppLinkService } from '../../core/services/app-link.service';
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-password-reset-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './password-reset-page.component.html',
  styleUrl: './password-reset-page.component.scss',
})
export class PasswordResetPageComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly auth = inject(AuthService);
  private readonly appLinks = inject(AppLinkService);

  private routeSubscription?: Subscription;

  checkingToken = true;
  submitting = false;
  canResetPassword = false;
  token = '';
  errorMessage = '';
  successMessage = '';
  readonly isAndroidDevice = this.appLinks.isAndroidDevice();
  readonly playStoreUrl = this.appLinks.playStoreUrl;
  readonly openAppUrl = this.appLinks.buildAuthAppLink('password-reset');

  form = this.fb.group({
    password: ['', [Validators.required, Validators.minLength(8)]],
    confirmPassword: ['', [Validators.required]],
  });

  ngOnInit(): void {
    this.routeSubscription = this.route.queryParamMap.subscribe((params) => {
      this.token = params.get('token')?.trim() ?? '';
      this.errorMessage = '';
      this.successMessage = '';

      if (!this.token) {
        this.checkingToken = false;
        this.canResetPassword = false;
        this.errorMessage = 'Le lien de réinitialisation est invalide ou incomplet.';
        return;
      }

      this.checkingToken = true;
      this.canResetPassword = false;
      this.auth.validatePasswordResetToken(this.token).subscribe({
        next: (isValid) => {
          this.checkingToken = false;
          this.canResetPassword = isValid;
          if (!isValid) {
            this.errorMessage = 'Ce lien de réinitialisation est invalide ou expiré.';
          }
        },
        error: () => {
          this.checkingToken = false;
          this.canResetPassword = false;
          this.errorMessage = 'Impossible de vérifier ce lien pour le moment.';
        },
      });
    });
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
  }

  submit(): void {
    if (this.submitting || !this.canResetPassword) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const password = this.form.controls.password.getRawValue() ?? '';
    const confirmPassword = this.form.controls.confirmPassword.getRawValue() ?? '';
    if (password !== confirmPassword) {
      this.errorMessage = 'Les mots de passe ne correspondent pas.';
      return;
    }

    this.submitting = true;
    this.errorMessage = '';

    this.auth.resetPassword(this.token, password).subscribe({
      next: (response) => {
        this.submitting = false;
        this.successMessage =
          response.message ?? 'Votre mot de passe a été mis à jour avec succès.';
        this.canResetPassword = false;
        this.form.reset();
      },
      error: (error) => {
        this.submitting = false;
        this.errorMessage = this.resolveError(error);
      },
    });
  }

  private resolveError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const backendMessage =
        typeof error.error === 'object' && error.error
          ? (error.error as { message?: string }).message
          : undefined;
      return backendMessage ?? 'Impossible de mettre à jour le mot de passe.';
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return 'Impossible de mettre à jour le mot de passe.';
  }
}
